import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { Plus, Globe, Pencil, Trash2, Search, Loader2, ChevronRight, KeyRound } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  EmptyState,
  Input,
  Label,
  Textarea,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui'
import { applicationsAPI, variantsAPI, testDataAPI, systemFixturesAPI } from '@/lib/api'
import type { IssueSourceProject } from '@/lib/api'
import { useAppContext } from '@/hooks/useAppContext'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { useToast } from '@/hooks/useToast'
import type { Application, Variant } from '@/types'
import { ConfirmDialog, TypedConfirmDialog } from '@/components/ConfirmDialog'
import { AppIntegrationsTab } from '@/pages/AppIntegrationsTab'
import { displaySignInUrl } from '@/pages/VariantCredentialsPanel'
import { NewAppWizard, type NewAppResult } from '@/components/NewAppWizard'
import {
  VariantFormFields,
  computeVariantFormState,
  isVariantFormComplete,
  variantFieldsFrom,
  type VariantFormState,
} from '@/components/VariantFormFields'

/* The 25 MB original-file cap is no longer declared here. It lives beside the
 * component that renders it and is imported above (#2158 slice 2): the
 * Proposed tab became a second caller, and two call sites each declaring the
 * value is two places to get it wrong. The backend's
 * `REFERENCE_DOC_ORIGINAL_SIZE_TOTAL_CAP` remains the authoritative gate; the
 * client constant is a display value that mirrors it.
 *
 * The secondary 500 KB extracted-text cap is enforced server-side
 * (planner-token budget bound) and only surfaces on rejection, not as a UI
 * display value, so it is not mirrored on the client at all. */

/** Application-domain labels are stored lowercase (the scan prompt's
 * example outputs are lowercase: "trademark clearance search").
 * Display them in sentence case so the form field reads naturally. */
function sentenceCase(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Compact "5 minutes ago" / "3 hours ago" / "2 days ago" formatter for
 * the "Last scanned from <variant>" indicator on the Application card.
 * Falls through to a date string for anything older than ~30 days so
 * users get a real timestamp rather than "47 days ago". */
function relativeTime(when: Date | string | undefined): string {
  if (!when) return ''
  const ts = typeof when === 'string' ? Date.parse(when) : when.getTime()
  if (!Number.isFinite(ts)) return ''
  const deltaMs = Date.now() - ts
  if (deltaMs < 0) return 'just now'
  const sec = Math.floor(deltaMs / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day} day${day === 1 ? '' : 's'} ago`
  return new Date(ts).toLocaleDateString()
}

/** Pick the most-recent scan across an app's variants. Returns the
 * variant + scan timestamp so the UI can render "from <variant.name>"
 * alongside the relative-time. Returns null when no variant has been
 * scanned, signaling the indicator should be hidden. */
function mostRecentScan(variants: Variant[]): { variant: Variant; scannedAt: Date | string } | null {
  let best: { variant: Variant; scannedAt: Date | string; ts: number } | null = null
  for (const v of variants) {
    const sa = v.scan?.scannedAt
    if (!sa) continue
    const ts = typeof sa === 'string' ? Date.parse(sa) : sa.getTime()
    if (!Number.isFinite(ts)) continue
    if (!best || ts > best.ts) best = { variant: v, scannedAt: sa, ts }
  }
  return best ? { variant: best.variant, scannedAt: best.scannedAt } : null
}

/** Read-only "Sign-in" echo for the Apps card (#1312 part F). Summarizes
 * how a variant signs in — which built-in helper or own login form, how
 * many roles, and the sign-in address — WITHOUT ever surfacing a secret.
 * The full editing surface lives on the Test Data → Credentials tab; this
 * tile is a glanceable pointer there. */
export interface VariantSignInSummary {
  roleCount: number
  /** Plain-language label of the sign-in method the first configured role
   * uses, or null when no role has one set up yet. */
  helperLabel: string | null
  /** A non-secret sign-in address to show (operator-typed or discovered),
   * if any. */
  address: string | null
}

/** Derive the Sign-in tile summary from a variant's credentials (as the
 * test-data read returns them) + its discovered addresses. Never reads a
 * secret field — only role names, the chosen/detected helper, and a
 * sign-in address. Exported for unit tests. */
export function summarizeVariantSignIn(
  credentials: Array<{
    role?: string
    loginFixtureBinding?: { mode?: string; libraryId?: string }
    steps?: Array<{ loginUrl?: string }>
    detectedSignInMethod?: { name?: string } | null
  }>,
  fixtureNameById: Map<string, string>,
  discoveredAddresses: string[],
): VariantSignInSummary {
  const roled = credentials.filter(c => (c.role || '').trim())
  let helperLabel: string | null = null
  let address: string | null = null
  for (const c of roled) {
    const b = c.loginFixtureBinding
    if (!helperLabel) {
      if (b?.mode === 'library' && b.libraryId) helperLabel = fixtureNameById.get(b.libraryId) ?? 'A built-in sign-in helper'
      else if (c.detectedSignInMethod?.name) helperLabel = c.detectedSignInMethod.name
      else if (b?.mode === 'none') helperLabel = 'Your own login form'
    }
    if (!address) {
      const typed = c.steps?.find(s => s.loginUrl && s.loginUrl.trim())?.loginUrl
      if (typed) address = typed.trim()
    }
  }
  if (!address && discoveredAddresses.length > 0) address = discoveredAddresses[0]
  return { roleCount: roled.length, helperLabel, address }
}

/** The read-only "Sign-in" tile shown beside the Framework tile on a
 * variant card (#1312 part F). Glanceable: which sign-in method + how
 * many roles + a sign-in address, never a secret. Clicking opens the
 * Credentials tab on Test Data for the variant. Hidden when no role is
 * set up. */
function VariantSignInTile({ summary, onOpen }: { summary?: VariantSignInSummary; onOpen: () => void }) {
  if (!summary || summary.roleCount === 0) return null
  return (
    <button
      type="button"
      onClick={onOpen}
      className="p-2 rounded border bg-background text-left hover:bg-muted/40 transition-colors"
      title="Open the Credentials tab on Test Data to set up sign-in"
    >
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        <KeyRound className="h-3 w-3" /> Sign-in
      </p>
      <p className="text-xs font-medium mt-0.5">{summary.helperLabel ?? 'Not set up yet'}</p>
      <p className="text-[10px] text-muted-foreground mt-0.5">
        {summary.roleCount} {summary.roleCount === 1 ? 'role' : 'roles'}
        {summary.address && <> · {displaySignInUrl(summary.address)}</>}
      </p>
    </button>
  )
}

function VariantIcon({ icon, size = 16 }: { icon?: string; size?: number }) {
  if (!icon) return null
  if (icon.startsWith('http')) {
    return <img src={icon} alt="" className="rounded" style={{ width: size, height: size }} />
  }
  return <span style={{ fontSize: size }}>{icon}</span>
}

/** Valid tab ids for the per-Application card (Slice 3D of #860;
 * `integrations` added in #1154, Slice 2 of meta #1152). The
 * `candidates` tab was removed in #1827 — candidate tests moved to the
 * Tests area as the Proposed stage, so the Apps page is pure
 * configuration; the old `?tab=candidates` deep link now redirects
 * there (see the redirect effect in `Applications`). `overview` is the
 * landing tab and the fallback for an unknown `?tab=...` query param.
 * Exported for unit tests. */
export const APP_TAB_IDS = [
  'overview',
  'variants',
  // 'reference-docs' retired (#2158 slice 2 follow-up). Documents moved to
  // Tests → Proposed, beside the suggestions they produce; leaving the tab
  // here as well gave two places to add the same thing. A stale `?tab=
  // reference-docs` link falls back to Overview via `normalizeAppTab`.
  'integrations',
] as const
export type AppTabId = (typeof APP_TAB_IDS)[number]

/** Map a raw `?tab=...` value to a known `AppTabId`, defaulting to
 * `overview` for `null` / `''` / unknown strings. Pure so a stale
 * bookmark from a future tab name doesn't crash the page — it just
 * lands on Overview. Exported for unit tests. */
export function normalizeAppTab(raw: string | null): AppTabId {
  return (APP_TAB_IDS as readonly string[]).includes(raw ?? '') ? (raw as AppTabId) : 'overview'
}

/** Build the `?…` search string that redirects the retired
 * Applications Candidate-Tests deep link (`?tab=candidates`) to
 * Tests → Proposed (#1827). Preserves the app + variant (as the Tests
 * page's `appId` / `variantId` handoff params) and any other query the
 * link arrived with (future filters), while dropping the now-dead
 * `tab` + `app` params. `?app=<id>` wins over the passed selection so
 * a deep link that named an app still lands on that app. Pure so the
 * redirect contract is unit-testable without rendering the page.
 * Exported for unit tests. */
export function buildProposedRedirectSearch(
  params: URLSearchParams,
  selectedAppId: string,
  selectedVariantId: string,
): string {
  const next = new URLSearchParams(params)
  next.delete('tab')
  const appId = next.get('app') ?? selectedAppId
  next.delete('app')
  next.set('stage', 'proposed')
  if (appId) next.set('appId', appId)
  if (selectedVariantId) next.set('variantId', selectedVariantId)
  return `?${next.toString()}`
}

/** Which variant a reference-doc row's View link should land on for
 * `appId`, or `undefined` when it cannot land anywhere (#2096).
 *
 * Tests → Proposed refuses to render without a selected variant, and an
 * application with no variants clears the global selection outright. So
 * the landing variant must belong to the application being linked to —
 * handing a row the header's variant is not enough. If the header sits
 * on app A and app B has zero variants, linking app B with A's variant
 * passes the Tests gate, then reconciliation finds nothing for B, drops
 * the selection, and the user meets the entry-barrier anyway (PR #2104
 * Codex review).
 *
 * Resolution order:
 *   1. The header's variant, but only when it belongs to this app —
 *      membership in this app's list is what proves that, so the
 *      selected APP id is not needed here.
 *   2. Otherwise this app's first variant.
 *   3. `undefined` when this app has none — the row shows its count and
 *      offers no link.
 *
 * An app whose variants have not loaded yet also yields `undefined`:
 * until the list arrives we cannot tell "none" from "not yet", and
 * withholding a link briefly is better than offering a dead one.
 *
 * Pure so the cross-app matrix is unit-testable without rendering the
 * page — same reason `buildProposedRedirectSearch` above is exported. */
export function pickLandingVariantId(
  variantsByApp: Record<string, ReadonlyArray<{ id: string }>>,
  appId: string,
  selectedVariantId: string,
): string | undefined {
  const list = variantsByApp[appId]
  if (!list || list.length === 0) return undefined
  if (selectedVariantId && list.some(v => v.id === selectedVariantId)) {
    return selectedVariantId
  }
  return list[0]?.id
}

export default function Applications() {
  const { apps, refreshApps, refreshApp, selectedAppId, selectedVariantId, setSelectedAppId, setSelectedVariantId, refreshVariants } = useAppContext()
  const toast = useToast()
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  const visibleApps = selectedAppId ? apps.filter(a => a.id === selectedAppId) : apps

  // Tabbed IA (Slice 3D of #860). One `?tab=...` query param drives
  // the active tab across every visible Application card — when the
  // user has drilled into a single app via the sidebar (the common
  // case) it acts like per-page state; in the multi-app list view
  // every card mirrors the same tab. This is a deliberate
  // simplification over per-card URL keys (which would balloon the
  // URL with `?tab-app1=variants&tab-app2=...`). Unknown values fall
  // back to `overview`, so a stale bookmark from a future tab name
  // doesn't break the page.
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = normalizeAppTab(searchParams.get('tab'))

  // #1827 — the Candidate-Tests tab moved to Tests → Proposed. An old
  // deep link (`?tab=candidates`) redirects there, carrying the app +
  // variant (as the Tests page's handoff params) and any other query
  // it arrived with (future filters) so the user lands on the same
  // scoped view. Runs before the tab strip renders anything, so the
  // stale tab id is never shown.
  useEffect(() => {
    if (searchParams.get('tab') !== 'candidates') return
    navigate(
      { pathname: wp('/tests'), search: buildProposedRedirectSearch(searchParams, selectedAppId, selectedVariantId) },
      { replace: true },
    )
  }, [searchParams, selectedAppId, selectedVariantId, navigate, wp])

  // #1165 — `?app=<id>` deep link reader. Workspace Integrations
  // oversight rows and the Variant Automation Notifications empty-state
  // pointer both link here with `?app=<id>&tab=integrations` so the
  // operator lands focused on the right app without having to drill
  // through the sidebar. Effect-driven (not derived) because
  // `setSelectedAppId` writes localStorage and shouldn't fire on every
  // render — we sync once when the param appears, then strip it so
  // a refresh doesn't override a later sidebar selection. Apps loaded
  // asynchronously: gate on `apps.length > 0` so we don't try to
  // select before the workspace's app list is in memory.
  const appQueryParam = searchParams.get('app')
  useEffect(() => {
    if (!appQueryParam || apps.length === 0) return
    if (!apps.some(a => a.id === appQueryParam)) return
    if (selectedAppId !== appQueryParam) {
      setSelectedAppId(appQueryParam)
    }
    // Strip the param so a later sidebar switch isn't fought by a
    // re-mount honoring a stale URL.
    const next = new URLSearchParams(searchParams)
    next.delete('app')
    setSearchParams(next, { replace: true })
    // selectedAppId intentionally omitted so this effect doesn't
    // refire after we set it — the strip-then-set sequence runs once
    // per unique `?app=` value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appQueryParam, apps])
  function setActiveTab(next: string) {
    const params = new URLSearchParams(searchParams)
    if (next === 'overview') {
      // Drop the param entirely for the default tab — keeps the URL
      // clean for the most common landing state.
      params.delete('tab')
    } else {
      params.set('tab', next)
    }
    setSearchParams(params, { replace: true })
  }

  // Variants per app — loaded lazily so the page doesn't depend on AppContext's single-app variants
  const [variantsByApp, setVariantsByApp] = useState<Record<string, Variant[]>>({})

  const [isAppDialogOpen, setIsAppDialogOpen] = useState(false)
  const [isNewAppOpen, setIsNewAppOpen] = useState(false)
  const [isVariantDialogOpen, setIsVariantDialogOpen] = useState(false)
  const [editingApp, setEditingApp] = useState<Application | null>(null)
  const [editingVariant, setEditingVariant] = useState<{ appId: string; variant: Variant | null } | null>(null)
  const [scanningVariantId, setScanningVariantId] = useState<string | null>(null)
  // Delete-confirmation dialog state (replaces the prior native
  // window.confirm() + window.prompt() modals which rendered as the
  // browser's OS-level dialog — ugly, can't be themed, jarring next
  // to our component library). One state per dialog type:
  //   - `confirmDeleteApp`     — routine delete-app Cancel/Confirm
  //   - `confirmDeleteVariant` — routine delete-variant Cancel/Confirm
  //                              (only for variants NOT marked
  //                              ciProtected; protected ones use the
  //                              typed-confirm flow below)
  //   - `confirmDeleteCiVariant` — typed-confirm for CI/CD-protected
  //                                variants where the operator has to
  //                                type the variant name verbatim
  const [confirmDeleteApp, setConfirmDeleteApp] = useState<{ id: string; name: string } | null>(null)
  const [confirmDeleteVariant, setConfirmDeleteVariant] = useState<{ appId: string; variantId: string; name: string } | null>(null)
  const [confirmDeleteCiVariant, setConfirmDeleteCiVariant] = useState<{ appId: string; variantId: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Load variants for all visible apps whenever the visible app list changes
  useEffect(() => {
    visibleApps.forEach(async (app) => {
      const list = await variantsAPI.getByApp(app.id)
      setVariantsByApp((prev) => ({ ...prev, [app.id]: list }))
    })
  }, [visibleApps.map(a => a.id).join(',')])

  async function reloadVariantsForApp(appId: string) {
    const list = await variantsAPI.getByApp(appId)
    setVariantsByApp((prev) => ({ ...prev, [appId]: list }))
    await refreshApps()
    if (appId === selectedAppId) {
      await refreshVariants()
    }
  }

  // #1312 part F — read-only Sign-in echo on each variant card. The
  // built-in sign-in helper catalog (names) is cross-workspace and
  // ~constant, so fetch it once; the per-variant credential summary is
  // fetched lazily for the visible apps' variants. Best-effort: a failed
  // fetch leaves the tile hidden. Never holds a secret — `summarizeVariantSignIn`
  // reads only roles, the chosen/detected helper, and a sign-in address.
  const [fixtureNameById, setFixtureNameById] = useState<Map<string, string>>(new Map())
  const [credsByVariant, setCredsByVariant] = useState<Record<string, any[]>>({})
  const fetchedVariantsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    systemFixturesAPI.list()
      .then(list => setFixtureNameById(new Map(list.map(f => [f.id, f.name]))))
      .catch(() => { /* tile falls back to detected names / hides */ })
  }, [])

  // Fetch each visible variant's credentials ONCE (PR #1323 review). This
  // effect re-runs as each app's variants arrive, so a ref guards against
  // re-fetching a variant already loaded, and the helper names loading
  // later does NOT trigger a refetch — the summary is derived below.
  useEffect(() => {
    let cancelled = false
    for (const app of visibleApps) {
      for (const variant of variantsByApp[app.id] ?? []) {
        if (fetchedVariantsRef.current.has(variant.id)) continue
        fetchedVariantsRef.current.add(variant.id)
        void testDataAPI.get(variant.id).then((doc: any) => {
          if (cancelled) return
          setCredsByVariant(prev => ({ ...prev, [variant.id]: doc.credentials ?? [] }))
        }).catch(() => { fetchedVariantsRef.current.delete(variant.id) /* allow a later retry */ })
      }
    }
    return () => { cancelled = true }
  }, [visibleApps.map(a => a.id).join(','), variantsByApp])

  // Derive each variant's Sign-in summary from its fetched credentials +
  // the separately-loaded helper names; recomputes when names arrive
  // without re-hitting the network.
  const signInByVariant = useMemo<Record<string, VariantSignInSummary>>(() => {
    const out: Record<string, VariantSignInSummary> = {}
    for (const app of visibleApps) {
      for (const variant of variantsByApp[app.id] ?? []) {
        const creds = credsByVariant[variant.id]
        if (!creds) continue
        const discovered = (variant.scan?.authCandidates ?? []).map((a: { url: string }) => a.url)
        out[variant.id] = summarizeVariantSignIn(creds, fixtureNameById, discovered)
      }
    }
    return out
  }, [visibleApps, variantsByApp, credsByVariant, fixtureNameById])

  /** Deep-link from a variant's Sign-in tile to its Credentials editor. */
  function openVariantSignIn(variantId: string) {
    setSelectedVariantId(variantId)
    navigate('/test-data?tab=credentials')
  }

  // Form state
  const [appName, setAppName] = useState('')
  const [appDescription, setAppDescription] = useState('')

  // The repository a failed test opens its issue in. The list comes from
  // the workspace's GitHub Issues connection, so the app holds no token.
  // `ftRepos` is null until loaded; `ftState` says why the picker can't
  // show when it can't.
  const [ftRepo, setFtRepo] = useState('')
  const [ftRepos, setFtRepos] = useState<IssueSourceProject[] | null>(null)
  const [ftState, setFtState] = useState<'loading' | 'ready' | 'no-connection' | 'error'>('loading')
  const ftLoad = useRef(0)

  const [variantForm, setVariantForm] = useState<VariantFormState>(() => computeVariantFormState(null))

  const loadApps = refreshApps

  // Reference-doc extraction polling used to live here (#945). It moved
  // to `useReferenceDocExtractionPolling`, called from Tests → Proposed,
  // by #2294. It is not called from this page any more: #2158 Slice 2
  // retired the reference-docs tab, so nothing here draws a document
  // status, and the loop was refreshing the whole application list every
  // three seconds to advance a pill this page no longer shows.

  /** Open the Edit Application dialog. A new app is made in the
   * two-step New App dialog (`NewAppWizard`), not here. */
  function openAppDialog(app: Application) {
    setEditingApp(app)
    setAppName(app.name)
    setAppDescription(app.description ?? '')
    void loadFailedTestRepos(app)
    setIsAppDialogOpen(true)
  }

  function openVariantDialog(appId: string, variant?: Variant) {
    setEditingVariant({ appId, variant: variant || null })
    setVariantForm(computeVariantFormState(variant))
    setIsVariantDialogOpen(true)
  }

  async function loadFailedTestRepos(app: Application) {
    // Each open of the dialog gets its own number; a reply for an earlier
    // open (another app, or this one closed and reopened) is ignored, so it
    // can never fill in the wrong app's picker.
    const load = ++ftLoad.current
    setFtRepo(app.failedTestRepositoryId ?? '')
    setFtRepos(null)
    setFtState('loading')
    try {
      const options = await applicationsAPI.getFailedTestRepositoryOptions(app.id)
      if (load !== ftLoad.current) return
      if (!options) {
        setFtState('no-connection')
        return
      }
      const { connectionId, repos } = options
      setFtRepos(repos)
      setFtState('ready')
      // Never picked (not None, which is saved as null): start on the
      // repository the app already reads requirements from, when it reads
      // exactly one through this connection.
      const source = app.issueSource
      if (app.failedTestRepositoryId === undefined && source?.connectionId === connectionId && source.projectKeys.length === 1) {
        const only = source.projectKeys[0]
        if (repos.some((r) => r.key === only)) setFtRepo(only)
      }
    } catch {
      if (load === ftLoad.current) setFtState('error')
    }
  }

  async function saveApp() {
    if (!editingApp) return
    try {
      await applicationsAPI.update(editingApp.id, {
        name: appName,
        description: appDescription || undefined,
        // Only once the list has loaded, so a failed load never clears a pick.
        ...(ftState === 'ready' ? { failedTestRepositoryId: ftRepo || null } : {}),
      })
    } catch (error) {
      console.error('Failed to save application:', error)
      toast({
        title: 'Failed to save application',
        description: 'Make sure the backend server is running.',
        variant: 'error',
      })
      return
    }
    setIsAppDialogOpen(false)
    // The refresh runs after the catch above, so a network blip after a
    // successful save is logged, not reported as a failed save.
    try {
      await loadApps()
    } catch (err) {
      console.error('Failed to refresh applications after save:', err)
    }
  }

  /** The New App dialog closed on a saved app: land the user on it.
   *
   * Order matters. Await the refresh so the new app is in `apps[]`
   * before selecting it: the header resolves its selection against that
   * list, and an id not yet in it would render "Select app…" for a frame.
   * A refresh failure is logged, not reported, because both saves worked;
   * the header shows the new app after the next reload. The list is not
   * patched by hand from the save replies, so it keeps one source.
   *
   * The variant is selected with the app, so the header never pairs the
   * new app with the previous app's variant. With no variant (the user
   * closed after the variant failed), the selection is cleared and the
   * Variants tab shows "No variants yet". Either way the page opens on
   * Variants, where a scan shows its progress. */
  async function handleAppCreated({ appId, variantId, scan }: NewAppResult) {
    try {
      await loadApps()
    } catch (err) {
      console.error('Failed to refresh applications after create:', err)
    }
    setSelectedAppId(appId)
    setSelectedVariantId(variantId ?? '')
    setActiveTab('variants')
    if (variantId && scan) scanVariant(appId, variantId)
  }

  async function saveVariant(andScan = false) {
    if (!editingVariant) return

    try {
      let variantId: string
      const fields = variantFieldsFrom(variantForm)
      if (editingVariant.variant) {
        await variantsAPI.update(editingVariant.variant.id, fields)
        variantId = editingVariant.variant.id
      } else {
        variantId = uuid()
        await variantsAPI.create({
          _id: variantId,
          applicationId: editingVariant.appId,
          ...fields,
        } as any)
      }
      const appId = editingVariant.appId
      setIsVariantDialogOpen(false)
      await reloadVariantsForApp(appId)
      if (andScan) scanVariant(appId, variantId)
    } catch (error) {
      console.error('Failed to save variant:', error)
      toast({
        title: 'Failed to save variant',
        description: 'Make sure the backend server is running.',
        variant: 'error',
      })
    }
  }

  /** Open the routine delete-app confirmation. The actual API call
   * runs from the dialog's onConfirm so the busy-state animation +
   * error handling live inside `<ConfirmDialog>` (rather than
   * scattered through this function and the dialog). */
  function deleteApp(id: string) {
    const app = apps.find(a => a.id === id)
    setConfirmDeleteApp({ id, name: app?.name ?? 'this application' })
  }

  /** Open the routine OR typed delete-variant confirmation depending
   * on the variant's `ciProtected` flag. CI-protected variants are
   * referenced by pipeline configurations — deleting one without a
   * deliberate confirm step risks breaking outside infrastructure,
   * so we require the operator to type the variant's name verbatim
   * (`TypedConfirmDialog`). Non-CI variants get the routine
   * Cancel/Delete flow. */
  async function deleteVariant(appId: string, variantId: string) {
    // Look up the live variant so we can read `ciProtected` + `name`.
    // The variant card render already has the row in memory, but a
    // fresh lookup is cheaper than threading the variant object
    // through this callback for the rare delete action.
    const variant = await variantsAPI.getById(variantId)
    if (!variant) {
      toast({ title: 'Variant not found', variant: 'error' })
      return
    }
    if (variant.ciProtected) {
      setConfirmDeleteCiVariant({ appId, variantId, name: variant.name })
    } else {
      setConfirmDeleteVariant({ appId, variantId, name: variant.name })
    }
  }

  const [scanSuccess, setScanSuccess] = useState<string | null>(null)

  async function scanVariant(appId: string, variantId: string) {
    setScanningVariantId(variantId)
    setScanSuccess(null)
    try {
      const result = await variantsAPI.rescan(variantId)
      // Targeted refresh — rescan now mutates two things: this variant's
      // scan fields, AND the parent Application doc when the auto-promote
      // helper writes to currently-empty applicationSummary / Domain /
      // UsageContext (PR #230). Full refreshApps() fan-out is still
      // wasted; refresh the one app + its variants instead.
      const [list] = await Promise.all([
        variantsAPI.getByApp(appId),
        refreshApp(appId),
      ])
      setVariantsByApp((prev) => ({ ...prev, [appId]: list }))
      if (appId === selectedAppId) {
        await refreshVariants()
      }
      setScanSuccess(result.scanLimited
        ? 'Scan complete (limited — we could not read much of the page)'
        : `Scan complete — ${result.framework || 'app'} detected`)
      setTimeout(() => setScanSuccess(null), 4000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setScanSuccess(null)
      toast({
        title: `Scan failed: ${msg}`,
        description:
          'This can happen if the URL is unreachable, behind a VPN, or requires interaction (cookie consent, CAPTCHA).',
        variant: 'error',
      })
    } finally {
      setScanningVariantId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Apps</h1>
          <p className="text-sm text-muted-foreground">
            Configure applications and variants
          </p>
        </div>
        <Button onClick={() => setIsNewAppOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New App
        </Button>
      </div>

      {apps.length === 0 ? (
        <EmptyState
          icon={Globe}
          title="No apps yet"
          body="Create your first app to start authoring tests against it."
          action={{ label: 'Create app', onClick: () => setIsNewAppOpen(true) }}
        />
      ) : (
        <div className="space-y-4">
          {visibleApps.map((app) => (
            <Card key={app.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <VariantIcon icon={variantsByApp[app.id]?.[0]?.icon || '🌐'} size={28} />
                    <div>
                      <CardTitle>{app.name}</CardTitle>
                      {app.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {app.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Edit application"
                      onClick={() => openAppDialog(app)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => deleteApp(app.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Tabbed IA (Slice 3D of #860). Each Application card
                    splits into Overview / Variants / Reference docs /
                    Integrations tabs — pure configuration since #1827
                    moved Candidate tests to Tests → Proposed. The same
                    `?tab=...` query drives every visible card (see
                    `setActiveTab` above for the rationale on a single
                    page-wide param vs per-card keys). Overview hosts the
                    auto-inferred context (the three textareas,
                    previously dominant on the page) as a collapsed
                    `<details>` disclosure so they stop competing
                    visually with reference docs + variants. */}
                {/* `defaultValue` is required by the Tabs prop type but
                    ignored in this controlled mode (we pass `value` +
                    `onValueChange` driven by the URL search param). */}
                <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="variants">
                      Variants
                      {(variantsByApp[app.id]?.length ?? 0) > 0 && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">
                          {variantsByApp[app.id]!.length}
                        </span>
                      )}
                    </TabsTrigger>
                    {/* Candidate tests moved to Tests → Proposed (#1827);
                        the Apps page is now pure configuration. */}
                    {/* Integrations tab (#1154 — Slice 2 of meta #1152).
                        New tab for the per-app GitHub repo + Slack
                        channel bindings. The small `new` pill stays
                        until the next release after this ship per the
                        mockup; remove it (or guard on a release flag)
                        once the tab is no longer the recently-shipped
                        surface. */}
                    <TabsTrigger value="integrations">
                      Integrations
                      <span className="ml-1.5 text-[9px] text-primary uppercase tracking-wide font-semibold">
                        new
                      </span>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-3 mt-4">
                    {/* Application context — canonical editable copies of the
                        three fields. Auto-promoted from variant scans when the
                        Application's field is empty (manual edits always win).
                        Per-variant scan.* sub-doc retains the per-URL audit
                        trail and is shown read-only on each variant below.

                        Keyed on the most-recent scan timestamp across the
                        app's variants so an auto-promote rescan remounts the
                        uncontrolled inputs and they pick up fresh
                        defaultValues. Field-save round-trips don't update the
                        variant scan timestamps, so editing one field doesn't
                        disrupt the others.

                        Hosted inside a `<details>` disclosure in Slice 3D:
                        the three textareas dominated the page pre-redesign,
                        but reference docs + future candidates carry more
                        of the planner-steering weight now. Defaulted closed
                        so the Overview tab is quiet by default; users who
                        do override the auto-inferred context open it once
                        and see the indicator below the summary. */}
                    {(() => {
                      const recent = mostRecentScan(variantsByApp[app.id] ?? [])
                      return (
                        <details
                          key={recent ? String(recent.scannedAt) : 'no-scan'}
                          className="group rounded-lg border border-border bg-card"
                        >
                          <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium hover:bg-muted/40 list-none flex items-center gap-2">
                            <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90 text-muted-foreground" />
                            <span>Auto-inferred context</span>
                            {recent && (
                              <span className="ml-auto text-[10px] font-normal text-muted-foreground">
                                Last scanned: from {recent.variant.name}, {relativeTime(recent.scannedAt)}
                              </span>
                            )}
                          </summary>
                          <div className="space-y-2 px-3 pb-3 pt-1">
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Application Summary</label>
                              <textarea
                                className="w-full text-xs bg-background border border-border rounded p-2 resize-none leading-relaxed focus:outline-hidden focus:border-primary/50"
                                rows={2}
                                maxLength={256}
                                placeholder="What the app does — auto-populated by the first variant scan; edit here to override."
                                defaultValue={app.applicationSummary || ''}
                                onBlur={(e) => {
                                  const val = e.target.value.trim()
                                  if (val !== (app.applicationSummary || '')) {
                                    applicationsAPI.update(app.id, { applicationSummary: val })
                                      .then(() => loadApps())
                                  }
                                }}
                              />
                            </div>

                            <div className="space-y-1">
                              <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Application Domain</label>
                              <input
                                type="text"
                                className="w-full text-xs bg-background border border-border rounded p-2 leading-relaxed focus:outline-hidden focus:border-primary/50"
                                placeholder="e.g. trademark clearance search"
                                maxLength={120}
                                defaultValue={sentenceCase(app.applicationDomain || '')}
                                onBlur={(e) => {
                                  const val = e.target.value.trim()
                                  if (val !== sentenceCase(app.applicationDomain || '')) {
                                    // Canonical storage is lowercase — the
                                    // sentence-cased displayed value would
                                    // otherwise round-trip back into Mongo
                                    // and drift the canonical copy.
                                    applicationsAPI.update(app.id, { applicationDomain: val.toLowerCase() })
                                      .then(() => loadApps())
                                  }
                                }}
                              />
                            </div>

                            <div className="space-y-1">
                              <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Application Usage Context</label>
                              <textarea
                                className="w-full text-xs bg-background border border-border rounded p-2 resize-none leading-relaxed focus:outline-hidden focus:border-primary/50"
                                rows={4}
                                maxLength={500}
                                placeholder="Primary users, what they value, high-risk defects, default test posture"
                                defaultValue={app.applicationUsageContext || ''}
                                onBlur={(e) => {
                                  const val = e.target.value.trim()
                                  if (val !== (app.applicationUsageContext || '')) {
                                    applicationsAPI.update(app.id, { applicationUsageContext: val })
                                      .then(() => loadApps())
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </details>
                      )
                    })()}
                  </TabsContent>


                  <TabsContent value="variants" className="space-y-2 mt-4">
                    <div className="flex items-center justify-end">
                      <Button variant="outline" size="sm" onClick={() => openVariantDialog(app.id)}>
                        <Plus className="h-3 w-3 mr-1" />
                        Add variant
                      </Button>
                    </div>
                    {(variantsByApp[app.id] ?? []).length === 0 ? (
                      <EmptyState
                        title="No variants yet"
                        body="Variants are environment-specific configurations of this app (production, staging, a feature branch). Add one to start authoring tests."
                        action={{ label: 'Add variant', onClick: () => openVariantDialog(app.id) }}
                      />
                    ) : (
                      <div className="space-y-3">
                        {(variantsByApp[app.id] ?? []).map((variant) => {
                            const scanning = scanningVariantId === variant.id
                            const scan = variant.scan
                            return (
                              <div key={variant.id} className="p-3 rounded-lg border bg-muted/30 space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <VariantIcon icon={variant.icon} />
                                    <span className="text-sm font-medium">{variant.name}</span>
                                    <Badge variant={variant.status} className="text-[10px]">{variant.status}</Badge>
                                    {variant.ciProtected && (
                                      <Badge
                                        variant="warning"
                                        className="text-[10px]"
                                        title="This variant is marked as a CI/CD entry point. Deletion requires name-typed confirmation."
                                      >
                                        CI
                                      </Badge>
                                    )}
                                    <span className="text-sm text-muted-foreground">{variant.baseUrl}</span>
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      title={scan ? 'Rescan' : 'Scan URL'}
                                      disabled={scanning}
                                      onClick={() => scanVariant(app.id, variant.id)}
                                    >
                                      {scanning
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <Search className="h-3 w-3" />}
                                    </Button>
                                    <Button variant="ghost" size="icon-sm" onClick={() => openVariantDialog(app.id, variant)}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button variant="ghost" size="icon-sm" onClick={() => deleteVariant(app.id, variant.id)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                {!scan && scanSuccess && scanningVariantId === null && (
                                  <p className="text-xs text-green-500 pl-1">{scanSuccess}</p>
                                )}
                                {/* Glanceable scan/sign-in tiles. Framework
                                    is the one scan signal worth surfacing
                                    (#614 follow-up); the Sign-in tile (#1312)
                                    sits beside it as a read-only echo of how
                                    the variant signs in, deep-linking to the
                                    Credentials tab — it shows the helper +
                                    role count + a sign-in address, never a
                                    secret. */}
                                {(scan?.framework || signInByVariant[variant.id]) && (
                                  <div className="flex flex-wrap gap-2 pt-1 border-t border-border/50 mt-2">
                                    {scan?.framework && (
                                      <div className="p-2 rounded border bg-background">
                                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Framework</p>
                                        <p className="text-xs font-medium mt-0.5">{scan.framework}</p>
                                      </div>
                                    )}
                                    <VariantSignInTile
                                      summary={signInByVariant[variant.id]}
                                      onOpen={() => openVariantSignIn(variant.id)}
                                    />
                                  </div>
                                )}

                              </div>
                            )
                          })}
                        </div>
                      )}
                  </TabsContent>

                  <TabsContent value="integrations" className="mt-4">
                    {/* Per-app GitHub + Slack bindings (#1154,
                        Slice 2 of meta #1152). The tab owns its own
                        fetch + save lifecycle; it re-fetches when the
                        operator switches apps (appId change) so the
                        oversight rows reflect the current app's
                        variants. */}
                    <AppIntegrationsTab appId={app.id} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* App Dialog */}
      <Dialog open={isAppDialogOpen} onOpenChange={setIsAppDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Edit Application
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="My App"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={appDescription}
                onChange={(e) => setAppDescription(e.target.value)}
                placeholder="Brief description of the application"
                rows={2}
              />
            </div>
            {/* Where a failed test opens its issue. Picked once the app exists,
                from the repositories the workspace's GitHub connection sees. */}
            {editingApp && (
              <div className="border-t pt-4 space-y-2">
                <Label className="text-sm font-medium" htmlFor="failed-test-repo">When a test fails, open a GitHub issue in</Label>
                {ftState === 'loading' && (
                  <p className="text-xs text-muted-foreground"><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Loading repositories…</p>
                )}
                {ftState === 'no-connection' && (
                  <p className="text-xs text-muted-foreground">
                    Connect GitHub in <Link to={wp('/settings?tab=workspace.integrations')} className="underline">Workspace Settings → Integrations</Link> to open an issue when a test fails.
                  </p>
                )}
                {ftState === 'error' && (
                  <p className="text-xs text-destructive">Couldn't load the repositories. Close this and try again.</p>
                )}
                {ftState === 'ready' && ftRepos && (
                  <>
                    <Select
                      id="failed-test-repo"
                      options={[
                        { value: '', label: 'None' },
                        ...ftRepos.map((r) => ({ value: r.key, label: r.name })),
                      ]}
                      value={ftRepo}
                      onChange={(e) => setFtRepo(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">The repositories your workspace's GitHub connection can see. No token is needed here.</p>
                  </>
                )}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAppDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveApp} disabled={!appName}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New App: the app and its first variant, in two steps */}
      <NewAppWizard open={isNewAppOpen} onOpenChange={setIsNewAppOpen} onCreated={handleAppCreated} />

      {/* Variant Dialog */}
      <Dialog open={isVariantDialogOpen} onOpenChange={setIsVariantDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVariant?.variant ? 'Edit Variant' : 'New Variant'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <VariantFormFields value={variantForm} onChange={setVariantForm} />
          </DialogBody>
          <DialogFooter className="flex items-center justify-between">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline"
              disabled={!isVariantFormComplete(variantForm)}
              onClick={() => saveVariant(false)}
            >
              Save without scanning
            </button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsVariantDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => saveVariant(true)} disabled={!isVariantFormComplete(variantForm)}>
                <Search className="h-3 w-3 mr-1" />
                Save &amp; Scan
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-app confirmation. Routine Cancel/Delete shape — the
          backend cascade-deletes the variants automatically. Wired
          via the shared `ConfirmDialog` so it matches the themed
          modal language the rest of the app uses (replaces the
          previous native window.confirm()). */}
      <ConfirmDialog
        open={confirmDeleteApp !== null}
        onClose={() => { if (!deleting) setConfirmDeleteApp(null) }}
        title="Delete this application?"
        description={
          <>
            This will permanently delete <strong>{confirmDeleteApp?.name}</strong> and all of its variants. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={async () => {
          if (!confirmDeleteApp) return
          setDeleting(true)
          try {
            await applicationsAPI.delete(confirmDeleteApp.id)
            // Await the refresh — `loadApps()` (alias for
            // `refreshApps`) returns a Promise. Without the await,
            // a refresh failure would become an unhandled rejection
            // AND the dialog would close before the list updated;
            // the user would briefly see the deleted app linger in
            // the grid until the underlying re-fetch settled.
            // PR #1046 Copilot review.
            await loadApps()
            setConfirmDeleteApp(null)
          } finally {
            setDeleting(false)
          }
        }}
      />

      {/* Delete-variant confirmation (routine, non-CI-protected).
          Same shape as the app-delete above; the variant's name
          appears in the body so the operator can spot a wrong
          target before they click Delete. */}
      <ConfirmDialog
        open={confirmDeleteVariant !== null}
        onClose={() => { if (!deleting) setConfirmDeleteVariant(null) }}
        title="Delete this variant?"
        description={
          <>
            This will permanently delete <strong>{confirmDeleteVariant?.name}</strong>. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={async () => {
          if (!confirmDeleteVariant) return
          setDeleting(true)
          try {
            await variantsAPI.delete(confirmDeleteVariant.variantId)
            await reloadVariantsForApp(confirmDeleteVariant.appId)
            setConfirmDeleteVariant(null)
          } finally {
            setDeleting(false)
          }
        }}
      />

      {/* Delete-variant confirmation (CI/CD-protected). The variant
          is marked as a pipeline entry-point; deleting it can break
          outside infrastructure that references it (ADR 0015 §
          referential integrity). Operator types the variant name
          to confirm; the destructive button only enables on a
          verbatim match. Replaces a window.prompt() — same gate,
          much better UX. */}
      <TypedConfirmDialog
        open={confirmDeleteCiVariant !== null}
        onClose={() => { if (!deleting) setConfirmDeleteCiVariant(null) }}
        title="Delete CI/CD-protected variant?"
        description={
          <>
            <strong>{confirmDeleteCiVariant?.name}</strong> is marked as a CI/CD entry point. Deleting it may break any pipeline that references it. Type the variant name to confirm.
          </>
        }
        expectedPhrase={confirmDeleteCiVariant?.name ?? ''}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={async () => {
          if (!confirmDeleteCiVariant) return
          setDeleting(true)
          try {
            await variantsAPI.delete(confirmDeleteCiVariant.variantId, {
              force: true,
              confirmName: confirmDeleteCiVariant.name,
            })
            await reloadVariantsForApp(confirmDeleteCiVariant.appId)
            setConfirmDeleteCiVariant(null)
          } finally {
            setDeleting(false)
          }
        }}
      />

    </div>
  )
}
